import { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  usePlaid,
  usePersonalLoan,
  usePersonalLoanRouteFsm,
  useAch,
  useTelemetry,
} from 'shared/hooks';
import { AchPay } from 'shared/constants';
import Disbursement from './Disbursement';

const DisbursementContainer = () => {
  const history = useHistory();
  const { uuid: personalLoanUuid } = useParams();
  const { loaded: plaidLinkLoaded, error: plaidLinkError, create: plaidLinkCreate } = usePlaid();
  const [plaidError, setPlaidError] = useState('');
  const {
    items: achItems = [],
    logins = [],
    getPlaidAssets,
    getAchLogins,
    updatePlaidItems,
    savePlaidAuthLink,
    deletePlaidItem,
    saveFailedPlaidLink,
    updatePlaidItem,
    error: achError,
  } = useAch();
  const { personalLoan, saveDisburseAchPlaid } = usePersonalLoan();
  const runRouter = usePersonalLoanRouteFsm();
  const { telemetry } = useTelemetry();

  const items = achItems
    .map((item) => ({
      ...item,
      login: logins.find((login) => login.itemId && login.itemId === item.itemId),
    }))
    .sort((i1, i2) => {
      if (i1.institution < i2.institution) return -1;
      if (i1.institution > i2.institution) return 1;
      return i1.uuid < i2.uuid ? -1 : 1;
    });

  const routingNumber = personalLoan.disburseAccount?.routingNumber;

  useEffect(() => {
    if (routingNumber) {
      personalLoan.payType === AchPay.AUTOPAY
        ? history.push(`/applications/${personalLoanUuid}/bank/autopay`)
        : runRouter();
    }
  }, [routingNumber, personalLoan.payType, history, personalLoanUuid, runRouter]);

  useEffect(() => {
    (async () => {
      const [assets, logins] = await Promise.all([getPlaidAssets(), getAchLogins()]);

      if (
        assets?.items.some(
          (item) =>
            item.assetsComplete &&
            !logins.find((login) => login.itemId && login.itemId === item.itemId)
        )
      ) {
        await updatePlaidItems();
      }
    })();
  }, [getAchLogins, getPlaidAssets, updatePlaidItems]);

  useEffect(() => {
    let mounted = true;
    let timeoutId;

    const pollHandler = () => {
      timeoutId = window.setTimeout(async () => {
        try {
          await Promise.all([getPlaidAssets(false), getAchLogins(false)]);
        } finally {
          if (mounted) pollHandler();
        }
      }, 3000);
    };

    pollHandler();

    return () => {
      window.clearTimeout(timeoutId);
      mounted = false;
    };
  }, [getAchLogins, getPlaidAssets]);

  const openPlaidLink = (
    item,
    onSuccess = async (accessToken, metadata) => {
      try {
        await handlePlaidSuccess(accessToken, metadata);
      } catch (e) {
        setPlaidError('Unable to retrieve accounts');
      }
    }
  ) =>
    plaidLinkCreate({
      itemUuid: item?.uuid,
      onSuccess,
      onExit: (error, meta) => {
        if (!error) history.push(`/applications/${personalLoanUuid}/bank/manual-disbursement`);
        if (error) handlePlaidError(error, meta);
      },
    });

  const handleSubmit = async (data) => {
    const { disburseAccount, payType } = await saveDisburseAchPlaid(data);
    telemetry('plDisbursement');
    // Plaid Assets don't have routing numbers, so the presense of routing number implies that Plaid Auth succeeded.
    const context = `/applications/${personalLoanUuid}/bank`;
    if (disburseAccount.routingNumber) {
      payType === 'AUTOPAY' ? history.push(`${context}/autopay`) : runRouter();
    } else {
      history.push(`${context}/confirm`);
    }
  };

  const handlePlaidSuccess = async (accessToken, metaData) => {
    await savePlaidAuthLink(accessToken, metaData);
    await getPlaidAssets();
  };

  const handlePlaidDelete = async (item) => {
    await deletePlaidItem(item.uuid);
    await getPlaidAssets();
  };

  const handlePlaidRetry = async (item) => {
    try {
      await updatePlaidItem(item.uuid);
      await getPlaidAssets();
    } catch (e) {
      setPlaidError('Unable to retry');
    }
  };

  const handlePlaidError = async (linkError, linkErrorMetadata) =>
    saveFailedPlaidLink({ linkError, linkErrorMetadata });

  // TODO: confirm copy for items
  const error = plaidError || plaidLinkError || achError;

  if (!plaidLinkLoaded || !personalLoan.uuid) return null;

  return (
    <Disbursement
      personalLoan={personalLoan}
      items={items}
      serverError={error}
      handleDelete={handlePlaidDelete}
      handleRetry={async (item) => {
        if (item.loginRequired) openPlaidLink(item, () => handlePlaidRetry(item));
        if (item.retryable) handlePlaidRetry(item);
      }}
      handleLink={() => openPlaidLink()}
      handleSubmit={handleSubmit}
    />
  );
};

export default DisbursementContainer;
